import React, { useCallback, useEffect, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { DiagnosticStatusRow } from "./DiagnosticStatusRow";
import { runDiagnostics } from "./diagnosticChecks";
import { resetAuthSession } from "./resetAuthSession";

const styles = {
  page: {
    minHeight: "100vh",
    backgroundColor: "#111827",
    color: "#ffffff",
    padding: "32px 20px",
  },
  container: {
    maxWidth: "980px",
    margin: "0 auto",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    gap: "16px",
    alignItems: "flex-start",
    marginBottom: "20px",
    flexWrap: "wrap",
  },
  title: {
    margin: 0,
    fontSize: "1.8rem",
    fontWeight: 800,
  },
  subtitle: {
    marginTop: "8px",
    color: "#a1acb7",
    maxWidth: "720px",
    lineHeight: 1.45,
  },
  actions: {
    display: "flex",
    gap: "10px",
    flexWrap: "wrap",
  },
  button: {
    border: 0,
    borderRadius: "8px",
    padding: "10px 14px",
    color: "#ffffff",
    backgroundColor: "#2563eb",
    fontWeight: 800,
    cursor: "pointer",
  },
  secondaryButton: {
    backgroundColor: "#374151",
  },
  dangerButton: {
    backgroundColor: "#b91c1c",
  },
  card: {
    backgroundColor: "#1f2937",
    border: "1px solid rgba(255,255,255,0.12)",
    borderRadius: "14px",
    padding: "20px",
    boxShadow: "0 14px 30px rgba(0,0,0,0.24)",
  },
  empty: {
    color: "#a1acb7",
    padding: "24px 0",
    lineHeight: 1.45,
  },
  summary: {
    display: "flex",
    gap: "12px",
    flexWrap: "wrap",
    marginBottom: "16px",
  },
  pill: {
    borderRadius: "999px",
    padding: "6px 10px",
    fontSize: "0.8rem",
    fontWeight: 800,
    backgroundColor: "rgba(255,255,255,0.08)",
  },
};

const buildSummary = (results) => {
  return results.reduce(
    (summary, result) => {
      summary[result.status] = (summary[result.status] || 0) + 1;
      return summary;
    },
    { pass: 0, warn: 0, fail: 0 }
  );
};

export const DiagnosticsPanel = () => {
  const { instance, accounts } = useMsal();
  const account = instance.getActiveAccount() || accounts[0] || null;
  const [results, setResults] = useState([]);
  const [running, setRunning] = useState(false);
  const [lastRunAt, setLastRunAt] = useState(null);

  const runChecks = useCallback(async () => {
    setRunning(true);
    setResults([
      {
        key: "diagnostics-running",
        label: "Diagnostics",
        status: "pending",
        message: "Running Tools diagnostics...",
      },
    ]);

    try {
      const nextResults = await runDiagnostics({ instance, account });
      setResults(nextResults);
      setLastRunAt(new Date());
    } catch (error) {
      console.error("Tools diagnostics failed", error);
      setResults([
        {
          key: "diagnostics-failed",
          label: "Diagnostics",
          status: "fail",
          message: "Unable to complete diagnostics",
          detail: error?.message || String(error),
        },
      ]);
    } finally {
      setRunning(false);
    }
  }, [instance, account]);

  useEffect(() => {
    runChecks();
  }, [runChecks]);

  const summary = buildSummary(results);

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <div style={styles.header}>
          <div>
            <h1 style={styles.title}>Tools Diagnostics</h1>
            <div style={styles.subtitle}>
              Check Microsoft sign-in, backend connectivity, phone config, browser
              calling support, and reminder readiness. This page does not place a
              call or send an SMS.
            </div>
          </div>
          <div style={styles.actions}>
            <button
              type="button"
              style={styles.button}
              onClick={runChecks}
              disabled={running}
            >
              {running ? "Running..." : "Run checks again"}
            </button>
            <button
              type="button"
              style={{ ...styles.button, ...styles.secondaryButton }}
              onClick={() => window.location.assign("/")}
            >
              Back to Tools
            </button>
            <button
              type="button"
              style={{ ...styles.button, ...styles.dangerButton }}
              onClick={() => resetAuthSession(instance)}
            >
              Reset sign-in session
            </button>
          </div>
        </div>

        <div style={styles.card}>
          {lastRunAt ? (
            <div style={styles.summary}>
              <span style={styles.pill}>Last run: {lastRunAt.toLocaleString()}</span>
              <span style={styles.pill}>Pass: {summary.pass || 0}</span>
              <span style={styles.pill}>Warnings: {summary.warn || 0}</span>
              <span style={styles.pill}>Failures: {summary.fail || 0}</span>
            </div>
          ) : null}

          {results.length ? (
            results.map((result) => (
              <DiagnosticStatusRow
                key={result.key}
                label={result.label}
                status={result.status}
                message={result.message}
                detail={result.detail}
              />
            ))
          ) : (
            <div style={styles.empty}>Diagnostics have not run yet.</div>
          )}
        </div>
      </div>
    </div>
  );
};
